import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "in_review", "approved", "rejected", "archived"];

export default function AdminAgents() {
    const [agents, setAgents] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkId = searchParams.get("id");

    const load = () =>
        api.get("/agents").then((r) => setAgents(r.data || [])).finally(() => setLoading(false));

    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (!deepLinkId || agents.length === 0) return;
        const match = agents.find((a) => a.id === deepLinkId);
        if (match) setSelected(match);
        else {
            toast.error("Application not found.");
            setSearchParams({}, { replace: true });
        }
    }, [deepLinkId, agents, setSearchParams]);

    const closeDetail = () => {
        setSelected(null);
        if (deepLinkId) setSearchParams({}, { replace: true });
    };

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/agents/${id}`, { status });
            toast.success("Status updated");
            await load();
            if (selected?.id === id) setSelected({ ...selected, status });
        } catch {
            toast.error("Could not update");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this application?")) return;
        try {
            await api.delete(`/agents/${id}`);
            toast.success("Deleted");
            closeDetail();
            await load();
        } catch {
            toast.error("Could not delete");
        }
    };

    return (
        <div className="p-10" data-testid="admin-agents">
            <h1 className="font-serif text-3xl text-brand-ink mb-2">Agent applications</h1>
            <p className="text-sm text-brand-muted mb-8">Realtors who've applied to join the referral network, newest first.</p>

            {loading ? (
                <div className="text-brand-muted text-sm">Loading…</div>
            ) : agents.length === 0 ? (
                <div className="text-brand-muted text-sm" data-testid="agents-empty">No applications yet.</div>
            ) : (
                <div className="border border-brand-line">
                    <table className="w-full text-sm">
                        <thead className="bg-brand-sand">
                            <tr className="text-left text-xs uppercase tracking-wider text-brand-muted">
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Brokerage</th>
                                <th className="px-4 py-3">Trade area</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Submitted</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {agents.map((a) => (
                                <tr
                                    key={a.id}
                                    className="border-t border-brand-line hover:bg-brand-sand/50 cursor-pointer"
                                    onClick={() => setSelected(a)}
                                    data-testid={`agent-row-${a.id}`}
                                >
                                    <td className="px-4 py-3 text-brand-ink">{a.first_name} {a.last_name}</td>
                                    <td className="px-4 py-3">{a.brokerage || "—"}</td>
                                    <td className="px-4 py-3">{a.trade_area || a.license_province || "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-block px-2 py-0.5 text-xs bg-brand-paper border border-brand-line">
                                            {a.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-brand-muted">
                                        {new Date(a.created_at).toLocaleDateString("en-CA")}
                                    </td>
                                    <td className="px-4 py-3 text-brand-ink underline">View</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && (
                <div
                    className="fixed inset-0 bg-brand-ink/50 z-50 flex items-end sm:items-center justify-center p-4"
                    onClick={closeDetail}
                    data-testid="agent-detail-modal"
                >
                    <div
                        className="bg-brand-paper border border-brand-line w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6 border-b border-brand-line flex items-center justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold">
                                    Application
                                </div>
                                <h2 className="font-serif text-2xl text-brand-ink mt-1">
                                    {selected.first_name} {selected.last_name}
                                </h2>
                            </div>
                            <button onClick={closeDetail} className="text-brand-muted hover:text-brand-ink">✕</button>
                        </div>
                        <div className="p-6 space-y-4 text-sm">
                            <Row label="Email" value={selected.email} />
                            <Row label="Phone" value={selected.phone} />
                            <Row label="Brokerage" value={selected.brokerage} />
                            <Row label="License province" value={selected.license_province} />
                            <Row label="Years licensed" value={selected.years_licensed} />
                            <Row label="Trade area" value={selected.trade_area} />
                            <Row label="Specializations" value={selected.specializations} />
                            <Row label="Annual volume" value={selected.annual_volume} />
                            <Row label="Buyer / Seller mix" value={selected.buyer_seller_mix} />
                            <Row label="Languages" value={selected.languages} />
                            <Row label="Website / LinkedIn" value={selected.website_or_linkedin} />
                            <Row label="Why a good fit" value={selected.why_good_fit} />
                            <div className="pt-4 border-t border-brand-line space-y-3">
                                <label className="block">
                                    <span className="text-xs uppercase tracking-wider text-brand-muted">Status</span>
                                    <select
                                        value={selected.status}
                                        onChange={(e) => updateStatus(selected.id, e.target.value)}
                                        data-testid="agent-status-select"
                                        className="mt-2 w-full bg-transparent border border-brand-line p-2 text-brand-ink"
                                    >
                                        {STATUSES.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </label>
                                <button
                                    onClick={() => remove(selected.id)}
                                    data-testid="agent-delete-button"
                                    className="text-sm text-red-700 hover:text-red-900"
                                >
                                    Delete application
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="grid grid-cols-3 gap-4">
            <div className="text-xs uppercase tracking-wider text-brand-muted">{label}</div>
            <div className="col-span-2 text-brand-ink whitespace-pre-wrap">{value || "—"}</div>
        </div>
    );
}
